import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import PageTracker from "./components/PageTracker";
import ErrorBoundary from "./components/ErrorBoundary";
import api from "./lib/api";

// Pages
import Landing from "./pages/Landing";
import Onboarding from "./pages/Onboarding";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import QuienSoy from "./pages/QuienSoy";
import Formaciones from "./pages/Formaciones";
import CourseDetail from "./pages/CourseDetail";
import Metodologia from "./pages/Metodologia";
import FAQ from "./pages/FAQ";
import Contacto from "./pages/Contacto";
import Dashboard from "./pages/Dashboard";
import MyCourses from "./pages/MyCourses";
import MiCamino from "./pages/MiCamino";
import CourseContent from "./pages/CourseContent";
import Profile from "./pages/Profile";
import MisFacturas from "./pages/MisFacturas";
import Checkout from "./pages/Checkout";

// Special Course Pages
import FacilitadorasLanding from "./pages/FacilitadorasLanding";
import FacilitadorasCheckout from "./pages/FacilitadorasCheckout";
import FacilitadorasCurso from "./pages/FacilitadorasCurso";

// Campus de Interioridad
import CampusInterioridad from "./pages/CampusInterioridad";
import CampusApp from "./pages/campus/CampusApp";

// Viajes Iniciáticos
import ViajesIniciaticos from "./pages/ViajesIniciaticos";

// Curso Límites
import CursoLimites from "./pages/CursoLimites";
import CursoLimites2026 from "./pages/CursoLimites2026";
import MasterclassLanding from "./pages/MasterclassLanding";
import MasterclassPrimerLimite from "./pages/MasterclassPrimerLimite";
import MasterclassProteccionEnergetica from "./pages/MasterclassProteccionEnergetica";
import WorkshopGracias from "./pages/WorkshopGracias";
import MagicRedeem from "./pages/MagicRedeem";
import AdminMasterclass from "./pages/admin/AdminMasterclass";
import AdminMasterclassEdit from "./pages/admin/AdminMasterclassEdit";
import AdminPaidWorkshopList from "./pages/admin/AdminPaidWorkshopList";
import AdminPaidWorkshopEdit from "./pages/admin/AdminPaidWorkshopEdit";
import AdminRetreats from "./pages/admin/AdminRetreats";
import { MasterclassHomeBanner } from "./components/MasterclassCard";
import RetreatLanding from "./pages/RetreatLanding";
import RetreatPrivate from "./pages/RetreatPrivate";
import RetreatHomeBanner from "./components/RetreatHomeBanner";

// Publicaciones / Manuales
import Publicaciones from "./pages/Publicaciones";

// Logo Showcase (temporary)
import LogoShowcase from "./pages/LogoShowcase";

// Color Test (temporary)
import ColorTest from "./pages/ColorTest";
import ColorTest2 from "./pages/ColorTest2";
import FacilitadorasColorTest from "./pages/FacilitadorasColorTest";
import ColorTestFinal from "./pages/ColorTestFinal";
import ColorTestAmatista from "./pages/ColorTestAmatista";
import ColorTestRosaPlata from "./pages/ColorTestRosaPlata";
import HomeProposals from "./pages/HomeProposals";
import HeroTest from "./pages/HeroTest";
import ImagePositionTest from "./pages/ImagePositionTest";
import HeroBackgroundTest from "./pages/HeroBackgroundTest";
import HeroSphereTest from "./pages/HeroSphereTest";
import TestimonialsCarouselTest from "./pages/TestimonialsCarouselTest";
import HeroImageTest from "./pages/HeroImageTest";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCampo from "./pages/admin/AdminCampo";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminCourseEdit from "./pages/admin/AdminCourseEdit";
import AdminCourseStudents from "./pages/admin/AdminCourseStudents";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminSpecialCourseEnrollments from "./pages/admin/AdminSpecialCourseEnrollments";
import AdminSpecialCourseEdit from "./pages/admin/AdminSpecialCourseEdit";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminStudentArea from "./pages/admin/AdminStudentArea";
import AdminLandingEditor from "./pages/admin/AdminLandingEditor";
import AdminSpecialPrograms from "./pages/admin/AdminSpecialPrograms";
import AdminFinanzas from "./pages/admin/AdminFinanzas";
import AdminMarketing from "./pages/admin/AdminMarketing";
import AdminCRM360Contacts from "./pages/admin/AdminCRM360Contacts";
import AdminContact360 from "./pages/admin/AdminContact360";
import AdminConfig from "./pages/admin/AdminConfig";
import AdminCampus from "./pages/admin/AdminCampus";

// Admin Layout
import AdminLayout from "./components/AdminLayout";

// Analytics
import { CookieBanner } from "./components/Analytics";

// Legal pages
import AvisoLegal from "./pages/legal/AvisoLegal";
import Privacidad from "./pages/legal/Privacidad";
import CookiesPolicy from "./pages/legal/Cookies";
import Terminos from "./pages/legal/Terminos";
import Desistimiento from "./pages/legal/Desistimiento";

// Tracking
import TrackingProvider from "./components/TrackingProvider";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[hsl(var(--text-secondary))]">Cargando...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Save the current URL to redirect back after login
    const currentPath = location.pathname + location.search;
    localStorage.setItem('auth_redirect', currentPath);
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[hsl(var(--text-secondary))]">Cargando...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/mi-camino" replace />;
  }
  
  return children;
};

// Layout with Navbar and Footer
const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col noise-overlay">
      <Navbar />
      <MasterclassHomeBanner />
      <main className="flex-1">
        {children}
      </main>
      {showFooter && <Footer />}
    </div>
  );
};

// App Content with Routes
const AppContent = () => {
  const { user, isAuthenticated, loading } = useAuth();
  
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await api.post('/seed');
      } catch (error) {
        // Ignore if already seeded
      }
    };
    seedData();
  }, []);
  
  // Show onboarding for authenticated users who haven't completed it
  const shouldShowOnboarding = isAuthenticated && user && !user.onboarding_completed;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[hsl(var(--text-secondary))] font-body">Cargando...</p>
        </div>
      </div>
    );
  }
  
  return (
    <>
    <PageTracker />
    <Routes>
      {/* Onboarding - Full screen, no navbar */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } 
      />
      
      {/* Auth pages */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Legal pages (public) */}
      <Route path="/aviso-legal" element={<AvisoLegal />} />
      <Route path="/privacidad" element={<Privacidad />} />
      <Route path="/cookies" element={<CookiesPolicy />} />
      <Route path="/terminos" element={<Terminos />} />
      <Route path="/desistimiento" element={<Desistimiento />} />
      
      {/* Public pages with layout */}
      <Route path="/" element={
        shouldShowOnboarding ? <Navigate to="/onboarding" replace /> : (
          <Layout>
            <Landing />
          </Layout>
        )
      } />
      
      <Route path="/quien-soy" element={
        <Layout>
          <QuienSoy />
        </Layout>
      } />
      
      <Route path="/campus-interioridad" element={
        <Layout>
          <CampusInterioridad />
        </Layout>
      } />

      {/* Campus Interno (suscriptoras) */}
      <Route path="/campus" element={<CampusApp />} />
      
      <Route path="/viajes" element={
        <Layout>
          <ViajesIniciaticos />
        </Layout>
      } />
      
      {/* Publicaciones / Manuales */}
      <Route path="/publicaciones" element={
        <Layout>
          <Publicaciones />
        </Layout>
      } />
      
      {/* Curso Límites - Landing dentro de la escuela */}
      <Route path="/formaciones/limites" element={
        <Layout showFooter={false}>
          <CursoLimites />
        </Layout>
      } />
      <Route path="/formaciones/curso-limites" element={<Navigate to="/formaciones/limites" replace />} />

      {/* LÍMITES 2ª edición 2026 — nueva formación en vivo */}
      <Route path="/formaciones/limites-2026" element={
        <Layout showFooter={true}>
          <CursoLimites2026 />
        </Layout>
      } />
      <Route path="/formaciones/limites-2a-edicion" element={<Navigate to="/formaciones/limites-2026" replace />} />

      {/* Masterclass gratuitas — multi-documento */}
      <Route path="/masterclass/el-primer-limite-volver-a-ti" element={
        <Layout>
          <MasterclassPrimerLimite />
        </Layout>
      } />
      {/* Taller de pago — Crea tu protección energética */}
      <Route path="/masterclass/crea-tu-proteccion-energetica" element={
        <Layout>
          <MasterclassProteccionEnergetica />
        </Layout>
      } />
      <Route path="/masterclass/:slug/gracias" element={
        <Layout>
          <WorkshopGracias />
        </Layout>
      } />
      <Route path="/magic" element={<MagicRedeem />} />
      <Route path="/masterclass/:slug" element={
        <Layout>
          <MasterclassLanding />
        </Layout>
      } />

      {/* Retiros presenciales */}
      <Route path="/retiros/:slug" element={
        <Layout>
          <RetreatLanding />
        </Layout>
      } />
      <Route path="/mi-retiro/:slug" element={
        <ProtectedRoute>
          <Layout>
            <RetreatPrivate />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/logo-showcase" element={<LogoShowcase />} />
      
      {/* Color Test - temporary */}
      <Route path="/color-test" element={
        <Layout>
          <ColorTest />
        </Layout>
      } />
      
      <Route path="/color-test-2" element={
        <Layout>
          <ColorTest2 />
        </Layout>
      } />
      
      <Route path="/facilitadoras-color-test" element={
        <Layout>
          <FacilitadorasColorTest />
        </Layout>
      } />
      
      <Route path="/color-test-final" element={
        <Layout>
          <ColorTestFinal />
        </Layout>
      } />
      
      <Route path="/color-test-amatista" element={
        <Layout>
          <ColorTestAmatista />
        </Layout>
      } />
      
      <Route path="/color-test-rosa-plata" element={
        <Layout>
          <ColorTestRosaPlata />
        </Layout>
      } />
      
      <Route path="/home-proposals" element={
        <Layout>
          <HomeProposals />
        </Layout>
      } />
      
      <Route path="/hero-test" element={
        <Layout>
          <HeroTest />
        </Layout>
      } />
      
      <Route path="/image-position-test" element={
        <Layout>
          <ImagePositionTest />
        </Layout>
      } />
      
      <Route path="/hero-background-test" element={
        <Layout>
          <HeroBackgroundTest />
        </Layout>
      } />
      
      <Route path="/hero-sphere-test" element={
        <Layout>
          <HeroSphereTest />
        </Layout>
      } />
      
      <Route path="/testimonials-test" element={
        <Layout>
          <TestimonialsCarouselTest />
        </Layout>
      } />
      
      <Route path="/hero-image-test" element={
        <Layout>
          <HeroImageTest />
        </Layout>
      } />
      
      <Route path="/formaciones" element={
        <Layout>
          <Formaciones />
        </Layout>
      } />
      
      {/* Special Course Routes - Dynamic slug */}
      <Route path="/formaciones/especiales/:slug" element={
        <Layout>
          <FacilitadorasLanding />
        </Layout>
      } />
      
      <Route path="/formaciones/especiales/:slug/inscripcion" element={
        <ProtectedRoute>
          <Layout>
            <FacilitadorasCheckout />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/formaciones/especiales/:slug/curso" element={
        <ProtectedRoute>
          <Layout showFooter={false}>
            <ErrorBoundary>
              <FacilitadorasCurso />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Generic route for all special courses - student area */}
      <Route path="/formaciones/:slug/curso" element={
        <ProtectedRoute>
          <Layout showFooter={false}>
            <ErrorBoundary>
              <FacilitadorasCurso />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Backward compatibility for original special course */}
      <Route path="/formaciones/facilitadoras-sosten-energetico" element={
        <Layout>
          <FacilitadorasLanding />
        </Layout>
      } />
      
      <Route path="/formaciones/facilitadoras-sosten-energetico/inscripcion" element={
        <ProtectedRoute>
          <Layout>
            <FacilitadorasCheckout />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/formaciones/:slug/inscripcion" element={
        <ProtectedRoute>
          <Layout>
            <FacilitadorasCheckout />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/formaciones/:courseId" element={
        <Layout>
          <CourseDetail />
        </Layout>
      } />
      
      <Route path="/metodologia" element={
        <Layout>
          <Metodologia />
        </Layout>
      } />
      
      <Route path="/faq" element={
        <Layout>
          <FAQ />
        </Layout>
      } />
      
      <Route path="/contacto" element={
        <Layout>
          <Contacto />
        </Layout>
      } />
      
      {/* Checkout — PUBLIC (identity + billing captured inline in the same page) */}
      <Route path="/checkout/:courseId" element={
        <Layout>
          <Checkout />
        </Layout>
      } />
      
      {/* Protected pages - Student area */}
      <Route path="/mi-camino" element={
        <ProtectedRoute>
          {shouldShowOnboarding ? <Navigate to="/onboarding" replace /> : (
            <Layout>
              <MiCamino />
            </Layout>
          )}
        </ProtectedRoute>
      } />
      
      {/* Backward compatibility redirects */}
      <Route path="/escuela" element={<Navigate to="/mi-camino" replace />} />
      <Route path="/mis-cursos" element={<Navigate to="/mi-camino" replace />} />
      
      <Route path="/curso/:courseId" element={
        <ProtectedRoute>
          <Layout showFooter={false}>
            <CourseContent />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/perfil" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/mis-facturas" element={
        <ProtectedRoute>
          <Layout>
            <MisFacturas />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes - Using AdminLayout */}
      <Route path="/admin" element={
        <AdminRoute>
          <Navbar />
          <AdminLayout />
        </AdminRoute>
      }>
        {/* Dashboard - CEO View */}
        <Route index element={<AdminDashboard />} />
        
        {/* El Campo - Corazón vivo */}
        <Route path="campo" element={<AdminCampo />} />
        
        {/* Cursos Module */}
        <Route path="cursos" element={<AdminCourses />} />
        <Route path="cursos/grabados" element={<AdminCourses />} />
        <Route path="cursos/nuevo" element={<AdminCourseEdit />} />
        <Route path="cursos/:courseId" element={<AdminCourseEdit />} />
        <Route path="cursos/:courseId/alumnos" element={<AdminCourseStudents />} />
        <Route path="formaciones-especiales" element={<AdminSpecialPrograms />} />
        <Route path="formaciones" element={<AdminSpecialPrograms />} />
        <Route path="formaciones/:slug" element={<AdminSpecialCourseEnrollments />} />
        <Route path="formaciones/:slug/editar" element={<AdminSpecialCourseEdit />} />
        <Route path="formaciones/:slug/modulos" element={<AdminStudentArea />} />
        <Route path="formaciones/:slug/inscripciones" element={<AdminSpecialCourseEnrollments />} />
        {/* Legacy routes - redirect to new structure */}
        <Route path="formacion-especial" element={<AdminSpecialCourseEnrollments />} />
        <Route path="formacion-especial/editar" element={<AdminSpecialCourseEdit />} />
        <Route path="formacion-especial/landing" element={<AdminLandingEditor />} />
        <Route path="formacion-especial/modulos" element={<AdminStudentArea />} />
        <Route path="formacion-especial/area-alumnas" element={<AdminStudentArea />} />

        {/* Masterclasses — CRUD */}
        <Route path="masterclass" element={<AdminMasterclass />} />
        <Route path="masterclass/:slug" element={<AdminMasterclassEdit />} />
        <Route path="paid-workshops" element={<AdminPaidWorkshopList />} />
        <Route path="paid-workshops/:slug" element={<AdminPaidWorkshopEdit />} />

        {/* Retiros presenciales — Super-panel */}
        <Route path="retiros" element={<AdminRetreats />} />
        <Route path="retiros/:slug" element={<AdminRetreats />} />
        
        {/* Alumnos Module - CRM */}
        <Route path="alumnos" element={<AdminUsers />} />
        <Route path="alumnos/:userId" element={<AdminUserDetail />} />
        <Route path="usuarios" element={<AdminUsers />} />
        <Route path="usuarios/:userId" element={<AdminUserDetail />} />
        
        {/* CRM 360 Module */}
        <Route path="crm" element={<AdminCRM360Contacts />} />
        <Route path="crm/contactos" element={<AdminCRM360Contacts />} />
        
        {/* Campus de Interioridad */}
        <Route path="campus" element={<AdminCampus />} />
        
        {/* Marketing Module */}
        <Route path="marketing" element={<AdminMarketing />} />
        <Route path="marketing/contactos/:contactId" element={<AdminContact360 />} />
        {/* Legacy routes redirected to unified marketing panel */}
        <Route path="marketing/suscriptores" element={<Navigate to="/admin/marketing" replace />} />
        <Route path="marketing/estadisticas" element={<Navigate to="/admin/marketing" replace />} />
        <Route path="marketing/campus-waitlist" element={<Navigate to="/admin/marketing" replace />} />
        <Route path="marketing/publicaciones-waitlist" element={<Navigate to="/admin/marketing" replace />} />
        
        {/* Finanzas Module */}
        <Route path="finanzas" element={<AdminFinanzas />} />
        <Route path="finanzas/facturas" element={<AdminFinanzas />} />
        <Route path="finanzas/gastos" element={<AdminFinanzas />} />
        <Route path="finanzas/impuestos" element={<AdminFinanzas />} />
        
        {/* Contenidos Module */}
        <Route path="contenidos" element={<AdminMessages />} />
        <Route path="contenidos/recursos" element={<AdminMessages />} />
        <Route path="mensajes" element={<AdminMessages />} />
        
        {/* Configuración Module */}
        <Route path="configuracion" element={<AdminConfig />} />
        <Route path="configuracion/fiscal" element={<AdminConfig />} />
        <Route path="configuracion/conexiones" element={<AdminConfig />} />
        <Route path="perfil" element={<AdminProfile />} />
      </Route>
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <TrackingProvider>
          <AppContent />
          <CookieBanner />
          <Toaster position="top-center" richColors />
        </TrackingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
